import React from 'react';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

interface PricingTier {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  highlighted: boolean;
}

interface PricingSectionProps {
  tiers: PricingTier[];
}

const PricingSection = ({ tiers }: PricingSectionProps) => {
  return (
    <section className="py-20 bg-card">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
            Transparante prijzen
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Kies het pakket dat bij uw bedrijf past
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
          {tiers.map((tier, index) => (
            <div
              key={index}
              className={`glassmorphic rounded-xl p-8 transition-all duration-300 ${
                tier.highlighted
                  ? 'border-2 border-primary shadow-premium scale-105'
                  : 'hover:shadow-premium hover:-translate-y-1'
              }`}
            >
              {tier.highlighted && (
                <div className="inline-block px-4 py-1 rounded-full bg-primary text-primary-foreground text-sm font-semibold mb-4">
                  Meest populair
                </div>
              )}
              
              <h3 className="text-2xl font-display font-bold text-foreground mb-2">
                {tier.name}
              </h3>
              
              <div className="mb-4">
                <span className="text-4xl font-display font-bold text-primary">
                  {tier.price}
                </span>
                <span className="text-muted-foreground ml-2">{tier.period}</span>
              </div>
              
              <p className="text-muted-foreground mb-6">
                {tier.description}
              </p>
              
              <ul className="space-y-3 mb-8">
                {tier.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={20} className="text-accent mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Link
                href="/contact"
                className={`block w-full py-3 rounded-lg font-semibold text-center transition-all duration-300 ${
                  tier.highlighted
                    ? 'bg-cta text-cta-foreground hover:bg-cta/90 hover:shadow-elevated'
                    : 'border border-primary text-primary hover:bg-primary hover:text-primary-foreground hover:shadow-premium'
                }`}
              >
                Kies {tier.name}
              </Link>
            </div>
          ))}
        </div>

        {/* Detailed Comparison Table */}
        <div className="max-w-6xl mx-auto mt-16">
          <h3 className="text-2xl font-display font-bold text-foreground mb-8 text-center">
            Gedetailleerde pakketvergelijking
          </h3>
          
          <div className="glassmorphic rounded-2xl overflow-hidden shadow-silver-glow border border-primary/20">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-primary/20 via-primary/15 to-primary/20 border-b-2 border-primary/30">
                    <th className="px-8 py-5 text-left text-sm font-display font-bold text-foreground uppercase tracking-wider">
                      <div className="flex items-center space-x-2">
                        <Icon name="ListBulletIcon" size={20} className="text-primary" />
                        <span>Functionaliteit</span>
                      </div>
                    </th>
                    {tiers.map((tier, index) => (
                      <th 
                        key={index}
                        className={`px-8 py-5 text-center text-sm font-display font-bold uppercase tracking-wider ${
                          tier.highlighted ? 'text-primary' : 'text-foreground'
                        }`}
                      >
                        <div className="flex flex-col items-center space-y-1">
                          <span>{tier.name}</span>
                          {tier.highlighted && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-accent/30 to-accent/20 text-accent text-xs font-bold">
                              <Icon name="StarIcon" size={12} className="mr-1" />
                              Aanbevolen
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/20">
                  {/* Extract unique features across all tiers */}
                  {Array.from(new Set(tiers.flatMap(tier => tier.features))).map((feature, featureIndex) => (
                    <tr 
                      key={featureIndex}
                      className="hover:bg-gradient-to-r hover:from-primary/10 hover:via-primary/5 hover:to-transparent transition-all duration-300 group"
                    >
                      <td className="px-8 py-5 text-sm text-foreground font-medium bg-gradient-to-r from-card/50 to-transparent">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-primary rounded-full group-hover:scale-150 transition-transform duration-300"></div>
                          <span>{feature}</span>
                        </div>
                      </td>
                      {tiers.map((tier, tierIndex) => (
                        <td key={tierIndex} className="px-8 py-5 text-center">
                          {tier.features.includes(feature) ? (
                            <div className="inline-flex items-center justify-center w-10 h-10 bg-gradient-to-br from-accent/30 to-accent/10 rounded-full group-hover:scale-110 transition-transform duration-300">
                              <Icon name="CheckCircleIcon" size={24} className="text-accent" />
                            </div>
                          ) : (
                            <div className="inline-flex items-center justify-center w-10 h-10 bg-muted-foreground/10 rounded-full">
                              <Icon name="XMarkIcon" size={20} className="text-muted-foreground/40" />
                            </div>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                  
                  {/* Pricing row */}
                  <tr className="bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 font-semibold border-t-2 border-primary/20">
                    <td className="px-8 py-6 text-sm text-foreground font-bold">
                      <div className="flex items-center space-x-2">
                        <Icon name="CurrencyEuroIcon" size={20} className="text-primary" />
                        <span>Prijs per maand</span>
                      </div>
                    </td>
                    {tiers.map((tier, index) => (
                      <td key={index} className="px-8 py-6 text-center">
                        <div className="inline-flex flex-col items-center space-y-1 px-4 py-2 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5">
                          <div className="text-2xl font-display font-bold text-primary">
                            {tier.price}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {tier.period}
                          </div>
                        </div>
                      </td>
                    ))}
                  </tr>
                  
                  {/* CTA row */}
                  <tr className="bg-card/50">
                    <td className="px-8 py-6 text-sm text-foreground font-bold">
                      <div className="flex items-center space-x-2">
                        <Icon name="RocketLaunchIcon" size={20} className="text-accent" />
                        <span>Actie</span>
                      </div>
                    </td>
                    {tiers.map((tier, index) => (
                      <td key={index} className="px-8 py-6 text-center">
                        <Link
                          href="/contact"
                          className={`inline-flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                            tier.highlighted
                              ? 'bg-gradient-to-r from-cta to-cta/80 text-cta-foreground hover:shadow-cta/50'
                              : 'border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground hover:shadow-primary/50'
                          }`}
                        >
                          <Icon name="ArrowRightIcon" size={16} />
                          <span>Kies pakket</span>
                        </Link>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        
        <div className="text-center mt-12">
          <Link
            href="/pricing"
            className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 transition-colors duration-300"
          >
            <span className="font-semibold">Bekijk volledige prijsoverzicht</span>
            <Icon name="ArrowRightIcon" size={20} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;